"use client";

import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiFetch, type ApiError } from "@/lib/api";
import { contactFormSchema, type ContactFormData } from "@/lib/validations/contact-form";
import { SOCIAL_FACEBOOK_URL, SOCIAL_INSTAGRAM_URL } from "@/lib/copy/client-platform-copy";
import { Mail, Send, CheckCircle, AlertCircle, Instagram, Facebook } from "lucide-react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import RecaptchaField, { type RecaptchaFieldRef } from "@/components/RecaptchaField";

interface FormStatus {
  type: "idle" | "loading" | "success" | "error";
  message: string;
}

export interface ContactPageClientProps {
  heroTitle?: string;
  heroSubtitle?: string;
}

export default function ContactPageClient({
  heroTitle = "Contact Us",
  heroSubtitle = "Have questions or need support? We're here to help! Get in touch with our friendly team.",
}: ContactPageClientProps) {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";
  const isRecaptchaConfigured = recaptchaSiteKey.trim().length > 0;
  const [status, setStatus] = useState<FormStatus>({
    type: "idle",
    message: "",
  });
  const [formStartTime, setFormStartTime] = useState<number>(0);
  const recaptchaRef = useRef<RecaptchaFieldRef>(null);

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      setStatus((prev) => (prev.type === "error" ? { type: "idle", message: "" } : prev));
    }
  };

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const messageValue = watch("message");

  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    if (!isRecaptchaConfigured) {
      setStatus({
        type: "error",
        message: "reCAPTCHA is not configured. Please contact support.",
      });
      return;
    }

    setStatus({ type: "loading", message: "Sending your message..." });

    // Invisible reCAPTCHA Enterprise: fetch a fresh token at submit time.
    const token = await recaptchaRef.current?.execute("contact");
    if (!token) {
      setStatus({
        type: "error",
        message: "reCAPTCHA verification failed. Please try again.",
      });
      return;
    }

    try {
      const submissionData = {
        ...data,
        timestamp: Date.now().toString(),
        jsEnabled: "true",
        website: "",
        confirm_email: "",
        captchaToken: token,
        token,
      };

      await apiFetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      setStatus({
        type: "success",
        message:
          "Thank you! Your message has been sent successfully. We'll get back to you soon.",
      });

      reset();
      resetCaptcha();
      setFormStartTime(Date.now());
    } catch (err) {
      const apiErr = err as ApiError;
      const fieldErrors = apiErr.errors && typeof apiErr.errors === "object" ? apiErr.errors : null;
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        let mappedToField = false;
        Object.entries(fieldErrors).forEach(([field, message]) => {
          if (field in data) {
            setError(field as keyof ContactFormData, {
              type: "server",
              message: message ?? "Invalid value",
            });
            mappedToField = true;
          }
        });
        const firstError = Object.values(fieldErrors)[0];
        setStatus({
          type: "error",
          message: mappedToField
            ? "Please fix the errors below and try again."
            : (firstError ?? apiErr.message ?? "Something went wrong. Please try again."),
        });
      } else {
        setStatus({
          type: "error",
          message: apiErr.message ?? "Something went wrong. Please try again.",
        });
      }
      resetCaptcha();
    }
  };

  return (
    <div>
      <section className="bg-gradient-to-br from-green-50 to-green-100 py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">{heroTitle}</h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">{heroSubtitle}</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-10">Get in Touch</h2>

              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Email Support</h3>
                    <p className="text-gray-600 text-lg mb-4 leading-relaxed">
                      Get help with your account, bookings, or listings. We typically respond within
                      24 hours.
                    </p>
                    <a
                      href="mailto:hello@rentitforward.com.au"
                      className="text-green-600 hover:text-green-700 text-lg font-medium transition-colors inline-flex items-center space-x-2"
                    >
                      <span>hello@rentitforward.com.au</span>
                      <Send className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h4>
                  <div className="space-y-3 text-gray-600">
                    <p className="text-lg font-semibold text-green-600">Rent It Forward</p>
                    <p className="text-lg">
                      <strong>ABN:</strong> 79 150 200 910
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Follow us</h4>
                  <p className="text-gray-600 mb-4">Stay up to date on Instagram and Facebook.</p>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={SOCIAL_INSTAGRAM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
                    >
                      <Instagram className="w-5 h-5" aria-hidden />
                      Instagram
                    </a>
                    <a
                      href={SOCIAL_FACEBOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
                    >
                      <Facebook className="w-5 h-5" aria-hidden />
                      Facebook
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card variant="elevated" padding="lg">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Send us a Message</h2>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 form-container">
                    <div className="absolute left-[-9999px] opacity-0 pointer-events-none">
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="w-0 h-0 overflow-hidden"
                      />
                      <input
                        type="email"
                        name="confirm_email"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="w-0 h-0 overflow-hidden"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="firstName"
                          className="block text-sm font-semibold text-gray-700 mb-2"
                        >
                          First Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          {...register("firstName")}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                            errors.firstName ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="Your first name"
                          maxLength={50}
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="lastName"
                          className="block text-sm font-semibold text-gray-700 mb-2"
                        >
                          Last Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          {...register("lastName")}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                            errors.lastName ? "border-red-500" : "border-gray-300"
                          }`}
                          placeholder="Your last name"
                          maxLength={50}
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Email Address <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        {...register("email")}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                          errors.email ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="your.email@example.com"
                        maxLength={100}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Subject <span className="text-red-500 ml-1">*</span>
                      </label>
                      <select
                        id="subject"
                        {...register("subject")}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                          errors.subject ? "border-red-500" : "border-gray-300"
                        }`}
                      >
                        <option value="">Select a topic</option>
                        <option value="General Question">General Question</option>
                        <option value="Booking Support">Booking Support</option>
                        <option value="Listing Help">Listing Help</option>
                        <option value="Payment Issue">Payment Issue</option>
                        <option value="Safety Concern">Safety Concern</option>
                        <option value="Technical Issue">Technical Issue</option>
                        <option value="Partnership Inquiry">Partnership Inquiry</option>
                        <option value="Feedback & Suggestions">Feedback & Suggestions</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.subject && (
                        <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Message <span className="text-red-500 ml-1">*</span>
                      </label>
                      <textarea
                        id="message"
                        {...register("message")}
                        rows={6}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none ${
                          errors.message ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="Tell us how we can help you..."
                        maxLength={1000}
                      />
                      <div className="flex justify-between mt-1">
                        {errors.message ? (
                          <p className="text-sm text-red-600">{errors.message.message}</p>
                        ) : (
                          <span />
                        )}
                        <span className="text-sm text-gray-500">{messageValue?.length ?? 0}/1000</span>
                      </div>
                    </div>

                    <div className="flex justify-center">
                      {isRecaptchaConfigured ? (
                        <RecaptchaField ref={recaptchaRef} onVerify={handleCaptchaChange} />
                      ) : (
                        <p className="text-sm text-red-600 mt-1 text-center">
                          reCAPTCHA is not configured. Set NEXT_PUBLIC_RECAPTCHA_SITE_KEY.
                        </p>
                      )}
                    </div>

                    {status.type !== "idle" && (
                      <div
                        className={`p-4 rounded-xl flex items-center space-x-3 ${
                          status.type === "success"
                            ? "bg-green-50 border border-green-200 text-green-800"
                            : status.type === "error"
                              ? "bg-red-50 border border-red-200 text-red-800"
                              : "bg-blue-50 border border-blue-200 text-blue-800"
                        }`}
                      >
                        {status.type === "success" && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        {status.type === "error" && (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        {status.type === "loading" && (
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        )}
                        <span className="font-medium">{status.message}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full py-4 text-lg font-semibold bg-green-600 hover:bg-green-700 transition-colors"
                      disabled={isSubmitting || !isRecaptchaConfigured}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Need Quick Answers?</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto">
              Check our FAQ section for instant answers to common questions, or browse our help
              articles for detailed guides.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-green-600 hover:bg-green-700 text-lg px-8 py-4"
              >
                <Link href="/faq">View FAQ</Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="border-green-600 text-green-600 hover:bg-green-700 hover:text-white text-lg px-8 py-4"
              >
                <Link href="/how-it-works">How It Works</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
